/**
 * Video text cues: show lines of text at given video timestamps; resets when video ends.
 *
 * Each line becomes visible at its data-cue-time and stays visible until its data-cue-time-end
 * (if set) or until the video restarts. Multiple lines can be visible at once.
 *
 *   <div data-video-text-cues>
 *     <video ...></video>
 *     <div class="cues">
 *       <p data-cue-time="0">First line</p>
 *       <p data-cue-time="3" data-cue-time-end="8">Second line (3s–8s)</p>
 *       <p data-cue-time="7">Third line (from 7s, no end)</p>
 *     </div>
 *   </div>
 *
 *   Optional: data-cue-time-end hides the line after that time (seconds).
 *   Optional: data-video-text-cues="selector" uses that selector for the video (default: "video").
 */
(function () {
  const ATTR_CONTAINER = 'data-video-text-cues';
  const ATTR_TIME = 'data-cue-time';
  const ATTR_TIME_END = 'data-cue-time-end';
  const CLASS_VISIBLE = 'is-visible';
  const CLASS_HIDDEN = 'is-hidden';

  function parseTime(val) {
    const n = parseFloat(val, 10);
    return Number.isFinite(n) ? n : -1;
  }

  function getCueElements(container) {
    const selector = `[${ATTR_TIME}]`;
    return Array.from(container.querySelectorAll(selector));
  }

  function getVideo(container) {
    const customSelector = container.getAttribute(ATTR_CONTAINER);
    const selector = customSelector && customSelector.trim() !== '' ? customSelector.trim() : 'video';
    return typeof selector === 'string' ? container.querySelector(selector) : null;
  }

  function buildCues(lines) {
    return lines
      .map(function (el) {
        const start = parseTime(el.getAttribute(ATTR_TIME));
        const endAttr = el.getAttribute(ATTR_TIME_END);
        const end = endAttr != null && endAttr !== '' ? parseTime(endAttr) : null;
        return { el, start, end };
      })
      .filter(function (c) { return c.start >= 0; })
      .sort(function (a, b) { return a.start - b.start; });
  }

  function setVisible(cueEl, visible) {
    var alreadyVisible = cueEl.classList.contains(CLASS_VISIBLE);
    if (visible) {
      cueEl.classList.remove(CLASS_HIDDEN);
      cueEl.classList.add(CLASS_VISIBLE);
      cueEl.setAttribute('aria-hidden', 'false');
      if (!alreadyVisible) {
        cueEl.style.opacity = '0';
        requestAnimationFrame(function () {
          cueEl.style.opacity = '';
        });
      }
    } else {
      cueEl.classList.remove(CLASS_VISIBLE);
      cueEl.classList.add(CLASS_HIDDEN);
      cueEl.setAttribute('aria-hidden', 'true');
      cueEl.style.opacity = '';
    }
  }

  function isCueVisible(cue, time) {
    if (time < cue.start) return false;
    if (cue.end != null && time >= cue.end) return false;
    return true;
  }

  function syncCues(cues, currentTime) {
    cues.forEach(function (c) {
      setVisible(c.el, isCueVisible(c, currentTime));
    });
  }

  function initContainer(container) {
    const video = getVideo(container);
    const lineEls = getCueElements(container);
    if (!video || lineEls.length === 0) return;

    const cues = buildCues(lineEls);
    lineEls.forEach(function (el) { setVisible(el, false); });

    function onTimeUpdate() {
      syncCues(cues, video.currentTime);
    }

    function onEnded() {
      video.currentTime = 0;
      syncCues(cues, 0);
    }

    function onSeeked() {
      syncCues(cues, video.currentTime);
    }

    video.addEventListener('timeupdate', onTimeUpdate, { passive: true });
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeked', onSeeked);

    if (video.readyState >= 2) {
      syncCues(cues, video.currentTime);
    } else {
      video.addEventListener('loadeddata', function once() {
        video.removeEventListener('loadeddata', once);
        syncCues(cues, video.currentTime);
      });
    }
  }

  function init() {
    document.querySelectorAll('[' + ATTR_CONTAINER + ']').forEach(initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[' + ATTR_CONTAINER + ']').forEach(initContainer);
  });
})();
